import { useState } from "react";
import { ShieldAlert, RefreshCw, Plus, Trash2, FlaskConical, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure, azErr } from "@/context/AzureContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

const STATE_LABEL = { enabled: "On", disabled: "Off", reportOnly: "Report-only" };
const STATE_STYLE = { enabled: "bg-emerald-100 text-emerald-700", disabled: "bg-muted text-muted-foreground", reportOnly: "bg-amber-100 text-amber-700" };
const CONTROL_LABEL = { mfa: "Require MFA", block: "Block access", compliantDevice: "Require compliant device", domainJoinedDevice: "Require Hybrid Entra joined device", passwordChange: "Require password change" };

const emptyForm = () => ({
  name: "", state: "enabled", include_all_users: true, groups: [], cloud_apps: ["All cloud apps"],
  sign_in_risk: [], grant_controls: ["mfa"],
});

export default function ConditionalAccess() {
  const { state, run, refresh } = useAzure();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [whatif, setWhatif] = useState({ user_id: "", app: "Microsoft Azure Management", sign_in_risk: "none", device_compliant: false });
  const [wfResult, setWfResult] = useState(null);

  const policies = state?.ca_policies || [];
  const groups = state?.entra_groups || [];
  const users = state?.entra_users || [];
  const cat = state?.identity_catalog || {};

  const toggleArr = (key, value) => setForm((f) => {
    const arr = f[key] || [];
    return { ...f, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
  });

  const submit = () =>
    run(() => api.post("/azure/identity/ca-policies", form), { success: `Policy '${form.name}' created` })
      .then((r) => { if (r.ok) setOpen(false); });

  const setState = (p, st) => run(() => api.patch(`/azure/identity/ca-policies/${p.id}`, { state: st }), { success: `Policy set to ${STATE_LABEL[st]}` });

  const del = (p) => { if (window.confirm(`Delete Conditional Access policy '${p.name}'?`)) run(() => api.delete(`/azure/identity/ca-policies/${p.id}`), { success: "Policy deleted" }); };

  const runWhatIf = async () => {
    if (!whatif.user_id) { toast.error("Select a user first."); return; }
    try {
      const { data } = await api.post("/azure/identity/ca-whatif", whatif);
      setWfResult(data);
    } catch (e) { toast.error(azErr(e)); }
  };

  return (
    <div data-testid="az-conditional-access">
      <Tabs defaultValue="policies">
        <TabsList>
          <TabsTrigger value="policies" data-testid="az-ca-tab-policies"><ShieldAlert className="h-4 w-4 mr-1.5" /> Policies</TabsTrigger>
          <TabsTrigger value="whatif" data-testid="az-ca-tab-whatif"><FlaskConical className="h-4 w-4 mr-1.5" /> What If</TabsTrigger>
        </TabsList>

        <TabsContent value="policies" className="mt-4">
          <div className="flex items-center gap-2 border-b border-border pb-3 mb-3">
            <Button size="sm" onClick={() => { setForm(emptyForm()); setOpen(true); }} data-testid="az-new-ca-btn" className="bg-[#0078d4] hover:bg-[#106ebe] text-white gap-2"><Plus className="h-4 w-4" /> New policy</Button>
            <Button size="sm" variant="ghost" onClick={refresh} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
          </div>
          <div className="border border-border rounded-md overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Policy name</TableHead><TableHead>State</TableHead><TableHead>Target apps</TableHead><TableHead>Controls</TableHead><TableHead className="w-40">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {policies.map((p) => (
                  <TableRow key={p.id} data-testid={`az-ca-row-${p.name}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell><Badge className={`text-xs ${STATE_STYLE[p.state]}`}>{STATE_LABEL[p.state]}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.cloud_apps.join(", ")}</TableCell>
                    <TableCell className="text-xs">{p.grant_controls.map((c) => CONTROL_LABEL[c] || c).join(", ")}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Select value={p.state} onValueChange={(v) => setState(p, v)}>
                          <SelectTrigger data-testid={`az-ca-state-${p.name}`} className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="enabled">On</SelectItem><SelectItem value="disabled">Off</SelectItem><SelectItem value="reportOnly">Report-only</SelectItem></SelectContent>
                        </Select>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" data-testid={`az-ca-delete-${p.name}`} onClick={() => del(p)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {policies.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No Conditional Access policies.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="whatif" className="mt-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-border rounded-lg bg-card p-4 space-y-3">
              <div className="font-semibold text-sm mb-1">Simulate a sign-in</div>
              <div className="space-y-1.5"><Label>User</Label>
                <Select value={whatif.user_id} onValueChange={(v) => setWhatif({ ...whatif, user_id: v })}>
                  <SelectTrigger data-testid="az-whatif-user"><SelectValue placeholder="Select a user" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.object_id} value={u.object_id}>{u.display_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Cloud app</Label>
                <Select value={whatif.app} onValueChange={(v) => setWhatif({ ...whatif, app: v })}>
                  <SelectTrigger data-testid="az-whatif-app"><SelectValue /></SelectTrigger>
                  <SelectContent>{(cat.cloud_apps || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Sign-in risk</Label>
                <Select value={whatif.sign_in_risk} onValueChange={(v) => setWhatif({ ...whatif, sign_in_risk: v })}>
                  <SelectTrigger data-testid="az-whatif-risk"><SelectValue /></SelectTrigger>
                  <SelectContent>{["none", "low", "medium", "high"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={whatif.device_compliant} onCheckedChange={(v) => setWhatif({ ...whatif, device_compliant: !!v })} data-testid="az-whatif-compliant" /> Device is compliant
              </label>
              <Button onClick={runWhatIf} data-testid="az-whatif-run" className="w-full bg-[#0078d4] hover:bg-[#106ebe] text-white gap-2"><FlaskConical className="h-4 w-4" /> Run what-if</Button>
            </div>

            <div className="border border-border rounded-lg bg-card p-4" data-testid="az-whatif-result">
              <div className="font-semibold text-sm mb-2">Evaluation result</div>
              {!wfResult ? <div className="text-sm text-muted-foreground">Run a simulation to see which policies apply.</div> : (
                <div className="space-y-3">
                  <div className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${wfResult.outcome === "blocked" ? "bg-red-100 text-red-700" : wfResult.outcome === "granted" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {wfResult.outcome === "blocked" ? <XCircle className="h-4 w-4" /> : wfResult.outcome === "granted" ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                    {wfResult.outcome === "blocked" ? "Access would be blocked" : wfResult.outcome === "granted" ? "Access granted" : "MFA / controls required"}
                  </div>
                  <div className="text-xs text-muted-foreground">{wfResult.user} → {wfResult.app} · risk {wfResult.sign_in_risk}</div>
                  <div>
                    <div className="text-xs font-semibold mb-1">Applied policies ({wfResult.policies.length})</div>
                    {wfResult.policies.length === 0 ? <div className="text-sm text-muted-foreground">No policies apply to this sign-in.</div> : (
                      <div className="space-y-1">
                        {wfResult.policies.map((p) => (
                          <div key={p.id} className="border border-border rounded-md px-3 py-2 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{p.name}</span>
                              {p.report_only && <Badge className="text-[10px] bg-amber-100 text-amber-700">Report-only</Badge>}
                              {p.satisfied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 ml-auto" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-600 ml-auto" />}
                            </div>
                            <div className="text-muted-foreground mt-0.5">Controls: {p.controls.map((c) => CONTROL_LABEL[c] || c).join(", ")}{p.unmet.length ? ` · unmet: ${p.unmet.join(", ")}` : ""}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>New Conditional Access policy</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Name *</Label><Input data-testid="az-ca-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="CA-Require-MFA-AzureMgmt" /></div>

            <div className="space-y-1.5">
              <Label>Assignments — Users</Label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.include_all_users} onCheckedChange={(v) => setForm({ ...form, include_all_users: !!v })} data-testid="az-ca-all-users" /> All users
              </label>
              {!form.include_all_users && (
                <div className="border border-border rounded-md p-2 space-y-1 max-h-32 overflow-y-auto">
                  <div className="text-[11px] text-muted-foreground mb-1">Target groups</div>
                  {groups.map((g) => (
                    <label key={g.object_id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={form.groups.includes(g.object_id)} onCheckedChange={() => toggleArr("groups", g.object_id)} /> {g.name}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Target cloud apps *</Label>
              <div className="border border-border rounded-md p-2 space-y-1">
                {(cat.cloud_apps || []).map((a) => (
                  <label key={a} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.cloud_apps.includes(a)} onCheckedChange={() => toggleArr("cloud_apps", a)} data-testid={`az-ca-app-${a.replace(/\s/g, "-")}`} /> {a}
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Grant controls *</Label>
              <div className="border border-border rounded-md p-2 space-y-1">
                {(cat.grant_controls || []).map((c) => (
                  <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.grant_controls.includes(c)} onCheckedChange={() => toggleArr("grant_controls", c)} data-testid={`az-ca-control-${c}`} /> {CONTROL_LABEL[c] || c}
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-1.5"><Label>Enable policy</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger data-testid="az-ca-state-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="enabled">On</SelectItem><SelectItem value="reportOnly">Report-only</SelectItem><SelectItem value="disabled">Off</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="az-ca-create-submit" onClick={submit} className="bg-[#0078d4] hover:bg-[#106ebe] text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
